import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { PublicBrandCollectionItemResponse } from './responses/public-brand-collection-item.response';
import { PublicBrandCategoryCollectionItemResponse } from './responses/public-brand-category-collection-item.response';
import { PublicBrandCosmeticCollectionItemResponse } from './responses/public-brand-cosmetic-collection-item.response';
import { BrandDetailsResponse } from '../../shared/api/responses/brand-details.response';

@Injectable({
  providedIn: 'root',
})
export class PublicBrandApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchDetailsBySlug(slug: string): Observable<BrandDetailsResponse> {
    const path = `cosmetic-brand/${HttpParams.Slug}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Slug]: slug,
    });
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(map(this.provider.httpHelper.bodyToResponse(BrandDetailsResponse)));
  }

  public fetchBrandCollection(
    request: CollectionRequest,
    cacheable = false,
  ): Observable<CollectionResponse<PublicBrandCollectionItemResponse>> {
    const path = 'cosmetic-brand';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(request),
    );
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => cacheable)
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            PublicBrandCollectionItemResponse,
          ),
        ),
      );
  }

  public fetchHighlightedBrandCollection(
    request: CollectionRequest,
    cacheable = false,
  ): Observable<CollectionResponse<PublicBrandCollectionItemResponse>> {
    const path = 'cosmetic-brand/highlighted';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(request),
    );
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => cacheable)
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            PublicBrandCollectionItemResponse,
          ),
        ),
      );
  }

  public fetchCategoryCollection(
    request: CollectionRequest,
    cacheable = false,
  ): Observable<CollectionResponse<PublicBrandCategoryCollectionItemResponse>> {
    const path = 'cosmetic-brand/category';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(request),
    );
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => cacheable)
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            PublicBrandCategoryCollectionItemResponse,
          ),
        ),
      );
  }

  /* prettier-ignore */
  public fetchCosmeticCollectionBySlug(
    slug: string,
    request: CollectionRequest,
    cacheable = false,
  ): Observable<CollectionResponse<PublicBrandCosmeticCollectionItemResponse>> {
    const path = `cosmetic-brand/${HttpParams.Slug}/cosmetic`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .path(path)
        .host(this.hostUrl())
        .params({ [HttpParams.Slug]: slug })
        .queryParams(this.provider.httpHelper.collectionRequestToQueryParams(request))
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => cacheable)
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            PublicBrandCosmeticCollectionItemResponse,
          ),
        ),
      );
  }

  protected hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
